import React, { useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Switch } from "@/components/ui/switch";
import { Checkbox } from "@/components/ui/checkbox";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { toast } from "sonner";
import { Trash, PencilSimple, UploadSimple, Plus, MagnifyingGlass, Image as ImageIcon, ChatText, Sparkle, Link as LinkIcon } from "@phosphor-icons/react";

const DOMAINS = [
    "Internal Audit Roles and Responsibilities",
    "Risk Management Governance",
    "Risk Management Assurance",
];

const EMPTY = {
    domain: DOMAINS[0],
    question: "",
    options: ["", "", "", ""],
    correct_index: 0,
    explanation: "",
    difficulty: "medium",
    is_free: false,
};

export default function Admin() {
    const [tab, setTab] = useState("add");
    const [questions, setQuestions] = useState([]);
    const [users, setUsers] = useState([]);
    const [stats, setStats] = useState(null);
    const [form, setForm] = useState(EMPTY);
    const [editingId, setEditingId] = useState(null);
    const [file, setFile] = useState(null);
    const [filterDomain, setFilterDomain] = useState("__all__");
    const [filterQuery, setFilterQuery] = useState("");
    const [logoPreview, setLogoPreview] = useState(null);
    const [logoFile, setLogoFile] = useState(null);
    const [currentLogo, setCurrentLogo] = useState(null);
    const [selectedIds, setSelectedIds] = useState(new Set());
    const [bulkDomain, setBulkDomain] = useState("");
    const [aiRunning, setAiRunning] = useState(false);
    const [missingCount, setMissingCount] = useState(0);

    const refresh = () => {
        api.get("/questions", { params: { limit: 5000 } }).then((r) => setQuestions(r.data.items || [])).catch(() => {});
        api.get("/admin/users").then((r) => setUsers(r.data.items || [])).catch(() => {});
        api.get("/admin/stats").then((r) => setStats(r.data)).catch(() => {});
    };

    useEffect(() => {
        refresh();
        api.get("/settings").then((r) => setCurrentLogo(r.data?.logo_data_url || null)).catch(() => {});
        api.get("/questions/missing-explanation-count").then((r) => setMissingCount(r.data.count || 0)).catch(() => {});
    }, []);

    const filtered = useMemo(() => {
        return questions.filter((q) => {
            if (filterDomain !== "__all__" && q.domain !== filterDomain) return false;
            if (filterQuery.trim() && !q.question.toLowerCase().includes(filterQuery.toLowerCase())) return false;
            return true;
        });
    }, [questions, filterDomain, filterQuery]);

    const changeDomain = async (q, newDomain) => {
        try {
            await api.patch(`/questions/${q.id}`, { domain: newDomain });
            toast.success("Domain updated");
            refresh();
        } catch {
            toast.error("Update failed");
        }
    };

    const changeDifficulty = async (q, difficulty) => {
        try {
            await api.patch(`/questions/${q.id}`, { difficulty });
            refresh();
        } catch {
            toast.error("Update failed");
        }
    };

    const generateOne = async (q) => {
        try {
            const { data } = await api.post(`/questions/${q.id}/generate-explanation`);
            toast.success("AI explanation added");
            refresh();
            const { data: mc } = await api.get("/questions/missing-explanation-count");
            setMissingCount(mc.count);
        } catch (e) {
            toast.error("Generation failed");
        }
    };

    const bulkGenerateAI = async () => {
        const ids = selectedIds.size > 0 ? Array.from(selectedIds) : null;
        if (!ids && !confirm(`Generate AI explanations for up to 25 questions missing one (${missingCount} remaining). Continue?`)) return;
        setAiRunning(true);
        try {
            const { data } = await api.post("/questions/bulk-generate-explanations", { ids, limit: 25 });
            toast.success(`Generated ${data.generated} · ${data.remaining_without_explanation} still missing`);
            setMissingCount(data.remaining_without_explanation);
            refresh();
        } catch {
            toast.error("Bulk generation failed");
        }
        setAiRunning(false);
    };

    const toggleFree = async (q) => {
        try {
            await api.patch(`/questions/${q.id}`, { is_free: !q.is_free });
            refresh();
        } catch {
            toast.error("Failed to toggle free");
        }
    };

    const saveExplanation = async (q, text) => {
        try {
            await api.patch(`/questions/${q.id}`, { explanation: text });
            toast.success("Explanation saved");
            refresh();
        } catch {
            toast.error("Save failed");
        }
    };

    const toggleSelect = (id) => {
        setSelectedIds((s) => {
            const n = new Set(s);
            if (n.has(id)) n.delete(id); else n.add(id);
            return n;
        });
    };

    const toggleSelectAllVisible = () => {
        const visibleIds = filtered.slice(0, 200).map((q) => q.id);
        const allSelected = visibleIds.every((id) => selectedIds.has(id));
        setSelectedIds((s) => {
            const n = new Set(s);
            if (allSelected) visibleIds.forEach((id) => n.delete(id));
            else visibleIds.forEach((id) => n.add(id));
            return n;
        });
    };

    const bulkReassign = async () => {
        if (!bulkDomain || selectedIds.size === 0) return;
        try {
            const { data } = await api.post("/questions/bulk-update", {
                ids: Array.from(selectedIds),
                domain: bulkDomain,
            });
            toast.success(`Reassigned ${data.modified} question${data.modified === 1 ? "" : "s"}`);
            setSelectedIds(new Set());
            setBulkDomain("");
            refresh();
        } catch {
            toast.error("Bulk update failed");
        }
    };

    const bulkFree = async (makeFree) => {
        if (selectedIds.size === 0) return;
        try {
            const { data } = await api.post("/questions/bulk-update", {
                ids: Array.from(selectedIds),
                is_free: makeFree,
            });
            toast.success(`Updated ${data.modified} question${data.modified === 1 ? "" : "s"}`);
            setSelectedIds(new Set());
            refresh();
        } catch {
            toast.error("Bulk update failed");
        }
    };

    const onLogoPick = (f) => {
        setLogoFile(f);
        if (!f) { setLogoPreview(null); return; }
        const reader = new FileReader();
        reader.onload = () => setLogoPreview(reader.result);
        reader.readAsDataURL(f);
    };

    const saveLogo = async () => {
        if (!logoPreview) return;
        try {
            await api.put("/settings/logo", { data_url: logoPreview });
            setCurrentLogo(logoPreview);
            setLogoPreview(null);
            setLogoFile(null);
            window.dispatchEvent(new Event("logo-updated"));
            toast.success("Logo updated");
        } catch (e) {
            toast.error("Failed to save logo (max ~1MB)");
        }
    };

    const removeLogo = async () => {
        if (!confirm("Remove custom logo and revert to default shield?")) return;
        try {
            await api.delete("/settings/logo");
            setCurrentLogo(null);
            window.dispatchEvent(new Event("logo-updated"));
            toast.success("Logo removed");
        } catch {
            toast.error("Failed to remove logo");
        }
    };

    const setOpt = (i, v) => {
        const opts = [...form.options];
        opts[i] = v;
        setForm({ ...form, options: opts });
    };

    const save = async () => {
        try {
            const payload = { ...form, options: form.options.filter((o) => o.trim()) };
            if (payload.correct_index >= payload.options.length) {
                toast.error("Correct index out of range");
                return;
            }
            if (editingId) {
                await api.put(`/questions/${editingId}`, payload);
                toast.success("Updated");
            } else {
                await api.post("/questions", payload);
                toast.success("Question added");
            }
            setForm(EMPTY);
            setEditingId(null);
            refresh();
        } catch (e) {
            toast.error("Save failed");
        }
    };

    const edit = (q) => {
        setEditingId(q.id);
        setForm({
            domain: q.domain,
            question: q.question,
            options: [...q.options, "", "", ""].slice(0, Math.max(4, q.options.length)),
            correct_index: q.correct_index,
            explanation: q.explanation || "",
            difficulty: q.difficulty || "medium",
            is_free: !!q.is_free,
        });
        setTab("add");
    };

    const remove = async (id) => {
        if (!confirm("Delete this question?")) return;
        await api.delete(`/questions/${id}`);
        toast.success("Deleted");
        refresh();
    };

    const upload = async () => {
        if (!file) return;
        const fd = new FormData();
        fd.append("file", file);
        try {
            const { data } = await api.post("/questions/bulk-import", fd, { headers: { "Content-Type": "multipart/form-data" } });
            toast.success(`Imported ${data.inserted} questions`);
            if (data.errors?.length) {
                toast.error(`${data.errors.length} errors — check console`);
                console.warn("Import errors:", data.errors);
            }
            refresh();
            setFile(null);
        } catch (e) {
            toast.error("Import failed");
        }
    };

    return (
        <div className="mx-auto max-w-7xl px-6 py-12">
            <div className="mb-8 flex flex-col items-start justify-between gap-4 md:flex-row md:items-center">
                <div>
                    <div className="overline mb-2 text-primary">Admin</div>
                    <h1 className="font-display text-4xl font-semibold tracking-tight">Question bank management</h1>
                </div>
                {stats && (
                    <div className="flex gap-6 text-sm">
                        <Stat label="Users" value={stats.users} />
                        <Stat label="Questions" value={stats.questions} />
                        <Stat label="Attempts" value={stats.attempts} />
                        <Stat label="Premium" value={stats.premium_users} />
                    </div>
                )}
            </div>

            <Tabs value={tab} onValueChange={setTab}>
                <TabsList data-testid="admin-tabs">
                    <TabsTrigger value="add" data-testid="tab-add">{editingId ? "Edit question" : "Add question"}</TabsTrigger>
                    <TabsTrigger value="list" data-testid="tab-list">All questions ({questions.length})</TabsTrigger>
                    <TabsTrigger value="import" data-testid="tab-import">Bulk import</TabsTrigger>
                    <TabsTrigger value="users" data-testid="tab-users">Users ({users.length})</TabsTrigger>
                    <TabsTrigger value="settings" data-testid="tab-settings">Settings</TabsTrigger>
                </TabsList>

                <TabsContent value="add">
                    <div className="mt-6 grid grid-cols-1 gap-4 border border-border bg-card p-8">
                        <div>
                            <Label>Domain</Label>
                            <Select value={form.domain} onValueChange={(v) => setForm({ ...form, domain: v })}>
                                <SelectTrigger data-testid="form-domain"><SelectValue /></SelectTrigger>
                                <SelectContent>
                                    {DOMAINS.map((d) => <SelectItem key={d} value={d}>{d}</SelectItem>)}
                                </SelectContent>
                            </Select>
                        </div>
                        <div>
                            <Label>Question</Label>
                            <Textarea value={form.question} onChange={(e) => setForm({ ...form, question: e.target.value })} rows={3} data-testid="form-question" />
                        </div>
                        {form.options.map((o, i) => (
                            <div key={i}>
                                <Label>Option {String.fromCharCode(65 + i)} {form.correct_index === i && <span className="text-primary">(correct)</span>}</Label>
                                <div className="flex gap-2">
                                    <Input value={o} onChange={(e) => setOpt(i, e.target.value)} data-testid={`form-option-${i}`} />
                                    <Button type="button" variant={form.correct_index === i ? "default" : "outline"} onClick={() => setForm({ ...form, correct_index: i })} data-testid={`form-correct-${i}`}>Correct</Button>
                                </div>
                            </div>
                        ))}
                        <div>
                            <Label>Explanation</Label>
                            <Textarea value={form.explanation} onChange={(e) => setForm({ ...form, explanation: e.target.value })} rows={3} data-testid="form-explanation" />
                        </div>
                        <div className="grid grid-cols-2 gap-4">
                            <div>
                                <Label>Difficulty</Label>
                                <Select value={form.difficulty} onValueChange={(v) => setForm({ ...form, difficulty: v })}>
                                    <SelectTrigger data-testid="form-difficulty"><SelectValue /></SelectTrigger>
                                    <SelectContent>
                                        <SelectItem value="easy">Easy</SelectItem>
                                        <SelectItem value="medium">Medium</SelectItem>
                                        <SelectItem value="hard">Hard</SelectItem>
                                    </SelectContent>
                                </Select>
                            </div>
                            <div className="flex items-center gap-3 pt-6">
                                <Switch checked={form.is_free} onCheckedChange={(v) => setForm({ ...form, is_free: v })} data-testid="form-is-free" />
                                <Label>Free (visible without premium)</Label>
                            </div>
                        </div>
                        <div className="flex gap-3">
                            <Button onClick={save} className="bg-primary text-primary-foreground hover:bg-primary/90" data-testid="form-save">
                                <Plus size={16} className="mr-2" /> {editingId ? "Update" : "Add question"}
                            </Button>
                            {editingId && <Button variant="ghost" onClick={() => { setForm(EMPTY); setEditingId(null); }} data-testid="form-cancel">Cancel</Button>}
                        </div>
                    </div>
                </TabsContent>

                <TabsContent value="list">
                    <div className="mt-6 flex flex-col gap-3 md:flex-row md:items-center">
                        <div className="relative flex-1">
                            <MagnifyingGlass size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
                            <Input
                                value={filterQuery}
                                onChange={(e) => setFilterQuery(e.target.value)}
                                placeholder="Search question text…"
                                className="pl-9"
                                data-testid="list-search"
                            />
                        </div>
                        <Select value={filterDomain} onValueChange={setFilterDomain}>
                            <SelectTrigger className="md:w-80" data-testid="list-filter-domain"><SelectValue /></SelectTrigger>
                            <SelectContent>
                                <SelectItem value="__all__">All domains ({questions.length})</SelectItem>
                                {DOMAINS.map((d) => (
                                    <SelectItem key={d} value={d}>{d} ({questions.filter((q) => q.domain === d).length})</SelectItem>
                                ))}
                            </SelectContent>
                        </Select>
                    </div>

                    <div className="mt-3 flex flex-wrap items-center justify-between gap-3 rounded-sm border border-primary/30 bg-primary/5 px-3 py-3" data-testid="ai-banner">
                        <div className="flex items-center gap-2 text-sm">
                            <Sparkle weight="duotone" className="text-primary" size={18} />
                            <span><strong>{missingCount}</strong> questions still need an explanation. Claude Sonnet can draft them for you.</span>
                        </div>
                        <Button size="sm" onClick={bulkGenerateAI} disabled={aiRunning || missingCount === 0} className="bg-primary text-primary-foreground hover:bg-primary/90" data-testid="ai-bulk-btn">
                            <Sparkle size={14} className="mr-1" /> {aiRunning ? "Generating…" : "Generate 25 more"}
                        </Button>
                    </div>

                    <div className="mt-3 flex flex-wrap items-center justify-between gap-3">
                        <div className="text-xs text-muted-foreground" data-testid="list-count">
                            Showing {filtered.length} of {questions.length} · {selectedIds.size} selected
                        </div>
                        {selectedIds.size > 0 && (
                            <div className="flex flex-wrap items-center gap-2 rounded-sm border border-primary/40 bg-primary/5 px-3 py-2" data-testid="bulk-bar">
                                <span className="text-xs font-medium">Bulk actions:</span>
                                <Select value={bulkDomain} onValueChange={setBulkDomain}>
                                    <SelectTrigger className="h-8 w-64 text-xs" data-testid="bulk-domain-select"><SelectValue placeholder="Reassign domain…" /></SelectTrigger>
                                    <SelectContent>
                                        {DOMAINS.map((d) => <SelectItem key={d} value={d}>{d}</SelectItem>)}
                                    </SelectContent>
                                </Select>
                                <Button size="sm" onClick={bulkReassign} disabled={!bulkDomain} className="bg-primary text-primary-foreground hover:bg-primary/90" data-testid="bulk-apply">Apply</Button>
                                <Button size="sm" variant="outline" onClick={() => bulkFree(true)} data-testid="bulk-make-free">Make free</Button>
                                <Button size="sm" variant="outline" onClick={() => bulkFree(false)} data-testid="bulk-make-premium">Make premium</Button>
                                <Button size="sm" variant="outline" onClick={bulkGenerateAI} disabled={aiRunning} data-testid="bulk-ai"><Sparkle size={12} className="mr-1" /> AI explain</Button>
                                <Button size="sm" variant="ghost" onClick={() => setSelectedIds(new Set())} data-testid="bulk-clear">Clear</Button>
                            </div>
                        )}
                    </div>

                    <div className="mt-3 border border-border bg-card">
                        <Table>
                            <TableHeader>
                                <TableRow>
                                    <TableHead className="w-10">
                                        <Checkbox
                                            checked={filtered.slice(0, 200).length > 0 && filtered.slice(0, 200).every((q) => selectedIds.has(q.id))}
                                            onCheckedChange={toggleSelectAllVisible}
                                            data-testid="select-all"
                                        />
                                    </TableHead>
                                    <TableHead>Question</TableHead>
                                    <TableHead className="w-64">Domain</TableHead>
                                    <TableHead className="w-32">Difficulty</TableHead>
                                    <TableHead className="w-20 text-center">Free</TableHead>
                                    <TableHead className="w-44 text-right">Actions</TableHead>
                                </TableRow>
                            </TableHeader>
                            <TableBody>
                                {filtered.slice(0, 200).map((q) => (
                                    <TableRow key={q.id} data-testid={`q-row-${q.id}`}>
                                        <TableCell>
                                            <Checkbox
                                                checked={selectedIds.has(q.id)}
                                                onCheckedChange={() => toggleSelect(q.id)}
                                                data-testid={`select-${q.id}`}
                                            />
                                        </TableCell>
                                        <TableCell className="max-w-md truncate">{q.question}</TableCell>
                                        <TableCell>
                                            <Select value={q.domain} onValueChange={(v) => changeDomain(q, v)}>
                                                <SelectTrigger className="h-8 text-xs" data-testid={`domain-select-${q.id}`}><SelectValue /></SelectTrigger>
                                                <SelectContent>
                                                    {DOMAINS.map((d) => <SelectItem key={d} value={d}>{d}</SelectItem>)}
                                                </SelectContent>
                                            </Select>
                                        </TableCell>
                                        <TableCell>
                                            <Select value={q.difficulty || "medium"} onValueChange={(v) => changeDifficulty(q, v)}>
                                                <SelectTrigger className="h-8 text-xs" data-testid={`diff-select-${q.id}`}><SelectValue /></SelectTrigger>
                                                <SelectContent>
                                                    <SelectItem value="easy">Easy</SelectItem>
                                                    <SelectItem value="medium">Medium</SelectItem>
                                                    <SelectItem value="hard">Hard</SelectItem>
                                                </SelectContent>
                                            </Select>
                                        </TableCell>
                                        <TableCell className="text-center">
                                            <Switch checked={!!q.is_free} onCheckedChange={() => toggleFree(q)} data-testid={`free-toggle-${q.id}`} />
                                        </TableCell>
                                        <TableCell className="text-right">
                                            <Button size="sm" variant="ghost" onClick={() => generateOne(q)} title="Generate AI explanation" data-testid={`ai-${q.id}`}>
                                                <Sparkle size={14} className="text-primary" />
                                            </Button>
                                            <ExplanationPopover q={q} onSave={saveExplanation} />
                                            <Button size="sm" variant="ghost" onClick={() => edit(q)} data-testid={`edit-${q.id}`}><PencilSimple size={14} /></Button>
                                            <Button size="sm" variant="ghost" onClick={() => remove(q.id)} data-testid={`del-${q.id}`}><Trash size={14} className="text-destructive" /></Button>
                                        </TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                        {filtered.length > 200 && (
                            <div className="border-t border-border p-3 text-center text-xs text-muted-foreground">
                                Showing first 200 rows. Refine your filter to narrow the list.
                            </div>
                        )}
                    </div>
                </TabsContent>

                <TabsContent value="import">
                    <div className="mt-6 space-y-4 border border-border bg-card p-8" data-testid="import-panel">
                        <p className="text-sm text-muted-foreground">
                            Upload CSV columns: <code className="font-mono text-xs">domain,question,option1,option2,option3,option4,correct_index,explanation,difficulty,is_free</code>
                            <br />Or JSON array of {`{domain, question, options[], correct_index, explanation, difficulty, is_free}`}.
                        </p>
                        <Input type="file" accept=".csv,.json" onChange={(e) => setFile(e.target.files?.[0] || null)} data-testid="import-file" />
                        <Button onClick={upload} disabled={!file} className="bg-primary text-primary-foreground hover:bg-primary/90" data-testid="import-btn">
                            <UploadSimple size={16} className="mr-2" /> Upload
                        </Button>
                    </div>
                </TabsContent>

                <TabsContent value="users">
                    <div className="mt-6 border border-border bg-card">
                        <Table>
                            <TableHeader>
                                <TableRow>
                                    <TableHead>Email</TableHead>
                                    <TableHead>Name</TableHead>
                                    <TableHead>Role</TableHead>
                                    <TableHead>Premium</TableHead>
                                    <TableHead>Created</TableHead>
                                </TableRow>
                            </TableHeader>
                            <TableBody>
                                {users.map((u) => (
                                    <TableRow key={u.id}>
                                        <TableCell>{u.email}</TableCell>
                                        <TableCell>{u.name}</TableCell>
                                        <TableCell>{u.role}</TableCell>
                                        <TableCell>{u.premium ? "Yes" : "No"}</TableCell>
                                        <TableCell className="text-xs text-muted-foreground">{u.created_at?.slice(0, 10)}</TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </div>
                </TabsContent>
                <TabsContent value="settings">
                    <div className="mt-6 space-y-6 border border-border bg-card p-8" data-testid="settings-panel">
                        <div>
                            <h3 className="font-display text-lg font-semibold">Brand logo</h3>
                            <p className="mt-1 text-sm text-muted-foreground">Upload a PNG or SVG (max ~1 MB). It replaces the shield icon in the header instantly.</p>
                        </div>

                        <div className="flex flex-wrap items-center gap-6">
                            <div className="flex h-24 w-40 items-center justify-center rounded-sm border border-border bg-background p-3">
                                {currentLogo || logoPreview ? (
                                    <img src={logoPreview || currentLogo} alt="Logo preview" className="max-h-full max-w-full object-contain" data-testid="logo-preview" />
                                ) : (
                                    <div className="flex flex-col items-center gap-1 text-muted-foreground">
                                        <ImageIcon size={22} />
                                        <span className="text-xs">No logo</span>
                                    </div>
                                )}
                            </div>
                            <div className="flex-1 space-y-3">
                                <Input type="file" accept="image/*" onChange={(e) => onLogoPick(e.target.files?.[0] || null)} data-testid="logo-file" />
                                <div className="flex gap-3">
                                    <Button onClick={saveLogo} disabled={!logoPreview} className="bg-primary text-primary-foreground hover:bg-primary/90" data-testid="logo-save">Save logo</Button>
                                    {currentLogo && <Button variant="outline" onClick={removeLogo} data-testid="logo-remove">Remove logo</Button>}
                                </div>
                            </div>
                        </div>
                    </div>
                </TabsContent>
            </Tabs>
        </div>
    );
}

function Stat({ label, value }) {
    return (
        <div className="text-right">
            <div className="overline text-muted-foreground">{label}</div>
            <div className="font-display text-2xl">{value}</div>
        </div>
    );
}

function ExplanationPopover({ q, onSave }) {
    const [open, setOpen] = useState(false);
    const [text, setText] = useState(q.explanation || "");
    useEffect(() => { setText(q.explanation || ""); }, [q.id, q.explanation]);
    const hasExpl = !!(q.explanation && q.explanation.trim());
    return (
        <Popover open={open} onOpenChange={setOpen}>
            <PopoverTrigger asChild>
                <Button size="sm" variant="ghost" data-testid={`explain-${q.id}`} title={hasExpl ? "Edit explanation" : "Add explanation"}>
                    <ChatText size={14} className={hasExpl ? "text-primary" : "text-muted-foreground"} />
                </Button>
            </PopoverTrigger>
            <PopoverContent className="w-96" side="left">
                <div className="space-y-3">
                    <div>
                        <div className="overline text-muted-foreground">Explanation</div>
                        <div className="mt-1 text-xs text-muted-foreground line-clamp-2">{q.question}</div>
                    </div>
                    <Textarea
                        rows={6}
                        value={text}
                        onChange={(e) => setText(e.target.value)}
                        placeholder="Paste or type the explanation…"
                        data-testid={`explain-textarea-${q.id}`}
                    />
                    <div className="flex justify-end gap-2">
                        <Button size="sm" variant="ghost" onClick={() => setOpen(false)}>Cancel</Button>
                        <Button
                            size="sm"
                            onClick={async () => { await onSave(q, text); setOpen(false); }}
                            className="bg-primary text-primary-foreground hover:bg-primary/90"
                            data-testid={`explain-save-${q.id}`}
                        >
                            Save
                        </Button>
                    </div>
                </div>
            </PopoverContent>
        </Popover>
    );
}
